import type { ScheduleEntry, ScheduleResult } from "@myapp/contract";

/**
 * Rewriting one entry inside a cached `ScheduleResult`. Pure, no React, no requests.
 *
 * This is what makes a reschedule land instantly. Without it the calendar keeps the entry's old
 * placement until `GET /schedule` answers, so a dropped block repaints where it came from and only
 * then jumps to where it was put — a flash of hundreds of milliseconds that reads as a bug.
 *
 * **The patch is derived from the request, never re-derived from the gesture.** `buildSchedulePatch`
 * already decides what a drag means for every entry kind, and the DTO it produces describes the
 * target state in full — so the caller hands that DTO's contents here rather than doing the geometry
 * a second time. One drag rule, one answer.
 *
 * Everything written here is provisional: the mutation's `onSettled` invalidation replaces the whole
 * result with the server's own, and `onError` restores the snapshot the caller took. Nothing keyed on
 * an id written here outlives the request — which is what makes this safe for absences, whose child
 * entry ids are recreated by every write.
 */

const MS_PER_MINUTE = 60_000;

/** Which entry to rewrite. The drag knows the entry id; a form knows its owner's. */
export type EntryMatch =
  | { entryId: number }
  | { appointmentId: number }
  | { taskId: number };

/**
 * The target placement, in the two shapes the API accepts.
 *
 * `SEGMENTED` is an anchor plus durations — the shape appointments and tasks use — and `SPAN` is the
 * plain start/end an absence has. `technicianId` is only set when the entry changes hands.
 */
export type EntryPatch =
  | {
      kind: "SEGMENTED";
      workStartsAt: string;
      travelMinutes: number;
      workMinutes: number;
      departureMinutes: number;
      /** Write the `actual*` fields instead of the planned ones — a started or completed block. */
      actuals?: boolean;
      technicianId?: number;
    }
  | { kind: "SPAN"; startsAt: string; endsAt: string; technicianId?: number };

/**
 * The occupied block a patch produces — what a calendar actually draws.
 *
 * `startsAt = workStartsAt − travelMinutes`, the rule `apps/web/CLAUDE.md` opens with: the work start
 * is not the block start, and mixing them up walks every entry backwards by its travel time.
 */
export function entryTimesFromPatch(patch: EntryPatch): { startsAt: string; endsAt: string } {
  if (patch.kind === "SPAN") {
    return { startsAt: patch.startsAt, endsAt: patch.endsAt };
  }

  const { workStartsAt, travelMinutes, workMinutes, departureMinutes } = patch;
  const workStartMs = new Date(workStartsAt).getTime();
  const startMs = workStartMs - travelMinutes * MS_PER_MINUTE;
  const endMs = startMs + (travelMinutes + workMinutes + departureMinutes) * MS_PER_MINUTE;

  return {
    startsAt: new Date(startMs).toISOString(),
    endsAt: new Date(endMs).toISOString(),
  };
}

function matches(entry: ScheduleEntry, match: EntryMatch): boolean {
  if ("entryId" in match) return entry.id === match.entryId;
  if ("appointmentId" in match) return entry.appointmentId === match.appointmentId;
  return entry.taskId === match.taskId;
}

/**
 * The entry with the patch applied. Occupancy is `COALESCE(actual, planned)` per component, so a
 * patch on a started block writes the `actual*` fields — writing the planned ones would update
 * values the server then ignores, and the block would not move.
 */
function patched(entry: ScheduleEntry, patch: EntryPatch): ScheduleEntry {
  const { startsAt, endsAt } = entryTimesFromPatch(patch);
  const base: ScheduleEntry = {
    ...entry,
    startsAt,
    endsAt,
    ...(patch.technicianId != null ? { technicianId: patch.technicianId } : {}),
  };

  // An absence has no travel or departure — the occupancy CHECK forces them to 0 — so its span is
  // the whole of it.
  if (patch.kind === "SPAN") return base;

  const { workStartsAt, travelMinutes, workMinutes, departureMinutes } = patch;

  return patch.actuals
    ? {
        ...base,
        actualWorkStartsAt: workStartsAt,
        actualTravelMinutes: travelMinutes,
        actualWorkMinutes: workMinutes,
        actualDepartureMinutes: departureMinutes,
      }
    : {
        ...base,
        plannedWorkStartsAt: workStartsAt,
        plannedTravelMinutes: travelMinutes,
        plannedWorkMinutes: workMinutes,
        plannedDepartureMinutes: departureMinutes,
      };
}

const byStart = (a: ScheduleEntry, b: ScheduleEntry) => a.startsAt.localeCompare(b.startsAt);

/**
 * The scheduling half of an owner's update DTO — an appointment or task form's submit — as a patch.
 *
 * `null` means "do not guess, wait for the server": no `plannedStart`, so the edit said nothing
 * about timing.
 *
 * The durations are required by both DTOs whenever a termin is described, but they are read
 * defensively: a partial patch that moved the start and omitted them would otherwise collapse the
 * block to nothing on screen.
 *
 * `isFullDay` used to bail out here, on the premise that the server derived the block rather than
 * taking the durations at face value. It never did — `allDay` is a passenger flag on the API, worth
 * two skipped checks and nothing else. The derivation lives in `features/appointment-upsert`
 * (`useFullDaySync`), so a Ganztägig submit now carries the same explicit minutes as any other and
 * lands optimistically like any other.
 */
export function ownerEntryPatch(dto: {
  plannedStart?: string | null;
  plannedTravelDuration?: number | null;
  plannedWorkDuration?: number | null;
  plannedDepartureDuration?: number | null;
  assignedTechnicianId?: number | null;
}): EntryPatch | null {
  if (!dto.plannedStart) return null;
  if (dto.plannedWorkDuration == null) return null;

  return {
    kind: "SEGMENTED",
    workStartsAt: dto.plannedStart,
    travelMinutes: dto.plannedTravelDuration ?? 0,
    workMinutes: dto.plannedWorkDuration,
    departureMinutes: dto.plannedDepartureDuration ?? 0,
    technicianId: dto.assignedTechnicianId ?? undefined,
  };
}

/**
 * The cached result with one entry moved, resized or reassigned.
 *
 * Returns the input untouched when nothing matches — an entry outside the loaded window, or a day
 * the user is not looking at — so the caller can apply this to every cached range without checking
 * first.
 *
 * A reassignment **moves the entry between technicians** rather than editing it in place: the row it
 * came from has to lose it, or the block would be drawn twice until the refetch lands.
 *
 * Entries stay sorted by `startsAt`. `packIntoLanes` sorts internally and does not care, but
 * `buildRows` walks the array in order to key a projected break by position, and re-sorting keeps
 * that key from shifting under an unrelated entry.
 */
export function applyEntryPatch(
  result: ScheduleResult,
  match: EntryMatch,
  patch: EntryPatch,
): ScheduleResult {
  const source = result.technicians.find((tech) => tech.entries.some((e) => matches(e, match)));
  if (!source) return result;

  const entry = source.entries.find((e) => matches(e, match)) as ScheduleEntry;

  // A block the technician has already started is drawn from its actuals, and occupancy prefers them
  // per component — so editing the *planned* values does not move it. Drawing the planned placement
  // here would put it somewhere the server is never going to agree with; leave it to the refetch.
  if (patch.kind === "SEGMENTED" && !patch.actuals && entry.actualWorkStartsAt != null) {
    return result;
  }

  const next = patched(entry, patch);
  const movedRows = next.technicianId !== entry.technicianId;

  return {
    ...result,
    technicians: result.technicians.map((tech) => {
      const isSource = tech.technicianId === source.technicianId;
      const isTarget = tech.technicianId === next.technicianId;
      if (!isSource && !isTarget) return tech;

      // Dropped from the row it left, appended to the row it joined; when neither applies it is the
      // same row and the entry is simply replaced.
      const entries = movedRows
        ? isSource
          ? tech.entries.filter((e) => !matches(e, match))
          : [...tech.entries, next]
        : tech.entries.map((e) => (matches(e, match) ? next : e));

      return { ...tech, entries: [...entries].sort(byStart) };
    }),
  };
}
